import { clsx } from 'clsx'
import { type FC, type JSX } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  Accordion,
  AccordionDetails,
  AccordionStack,
  AccordionSummary,
  Link,
} from 'base-ui'

import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../../../global-config'
import { LOCALE } from '../../../../../i18n'
import { urlResolverGetMediServiceRxLandingPageSecondQuestionPartnerLink } from '../../../../../url-handling'

import styles from './Faq.module.css'

type QaSectionData = {
  answer: JSX.Element
  key: string
  question: JSX.Element
}

const getWhatAdvantagesQuestion = (
  config: PublicGlobalConfig,
): QaSectionData => {
  return {
    answer: (
      <FormattedMessage
        id="chrx.faq.a1.new"
        values={{
          linkToPartner: (text) => (
            <Link
              href={
                config.locale === LOCALE.FR_CH
                  ? 'https://www.mediservice.ch/fr/'
                  : 'https://www.mediservice.ch/de/'
              }
              rel="noreferrer"
              target="_blank"
            >
              {text}
            </Link>
          ),
          linkToPharmaceuticalSecurity: (text) => (
            <Link
              href={
                config.locale === LOCALE.FR_CH
                  ? 'https://www.mediservice.ch/fr/pharmacie/controles-de-securite-pharmaceutiques.php'
                  : 'https://www.mediservice.ch/de/apotheke/pharmazeutische-sicherheitschecks.php'
              }
              rel="noreferrer"
              target="_blank"
            >
              {text}
            </Link>
          ),
        }}
      />
    ),
    key: 'rx.ch.faq.question.1',
    question: <FormattedMessage id="rx.ch.faq.question.1" />,
  }
}

const getHowToBecomeCustomerQuestion = (
  config: PublicGlobalConfig,
): QaSectionData => {
  return {
    answer: (
      <p>
        <FormattedMessage
          id="rx.ch.faq.answer.2.new"
          values={{
            link: (text) => (
              <Link
                href={urlResolverGetMediServiceRxLandingPageSecondQuestionPartnerLink(
                  config,
                )}
                rel="noreferrer"
                target="_blank"
              >
                {text}
              </Link>
            ),
          }}
        />
      </p>
    ),
    key: 'rx.ch.faq.question.2',
    question: <FormattedMessage id="rx.ch.faq.question.2" />,
  }
}

const getWhoWillLookAfterMeQuestion = (): QaSectionData => {
  return {
    answer: (
      <p>
        <FormattedMessage id="rx.ch.faq.answer.3" />
      </p>
    ),
    key: 'rx.ch.faq.question.3',
    question: <FormattedMessage id="rx.ch.faq.question.3" />,
  }
}

const getIsCopyRequiredQuestion = (): QaSectionData => {
  return {
    answer: <FormattedMessage id="rx.ch.faq.answer.4" />,
    key: 'rx.ch.faq.question.4',
    question: <FormattedMessage id="rx.ch.faq.question.4" />,
  }
}

const getPrescriptionAlreadyAtPharmacyQuestion = (): QaSectionData => {
  return {
    answer: <FormattedMessage id="rx.ch.faq.answer.11" />,
    key: 'rx.ch.faq.question.11',
    question: <FormattedMessage id="rx.ch.faq.question.11" />,
  }
}

const getHowLongIsValidQuestion = (): QaSectionData => {
  return {
    answer: <FormattedMessage id="rx.ch.faq.answer.10" />,
    key: 'rx.ch.faq.question.10',
    question: <FormattedMessage id="rx.ch.faq.question.10" />,
  }
}

const getAvailabilityQuestion = (): QaSectionData => {
  return {
    answer: <FormattedMessage id="rx.ch.faq.answer.9" />,
    key: 'rx.ch.faq.question.9',
    question: <FormattedMessage id="rx.ch.faq.question.9" />,
  }
}

const getAreAllMedicationsDeliveredQuestion = (): QaSectionData => {
  return {
    answer: <FormattedMessage id="rx.ch.faq.answer.5" />,
    key: 'rx.ch.faq.question.5',
    question: <FormattedMessage id="rx.ch.faq.question.5" />,
  }
}

const getOtherWaysToSubmitQuestion = (): QaSectionData => {
  return {
    answer: <FormattedMessage id="rx.ch.faq.answer.8" />,
    key: 'rx.ch.faq.question.8',
    question: <FormattedMessage id="rx.ch.faq.question.8" />,
  }
}

const getHowFastDeliveryQuestion = (): QaSectionData => {
  return {
    answer: (
      <p>
        <FormattedMessage id="rx.ch.faq.answer.6.new" />
      </p>
    ),
    key: 'rx.ch.faq.question.6',
    question: <FormattedMessage id="rx.ch.faq.question.6" />,
  }
}

const getNotAtHomeQuestion = (): QaSectionData => {
  return {
    answer: <FormattedMessage id="rx.ch.faq.answer.7" />,
    key: 'rx.ch.faq.question.7',
    question: <FormattedMessage id="rx.ch.faq.question.7" />,
  }
}

const faqQuestions = (config: PublicGlobalConfig): QaSectionData[] => [
  getWhatAdvantagesQuestion(config),
  getHowToBecomeCustomerQuestion(config),
  getWhoWillLookAfterMeQuestion(),
  getIsCopyRequiredQuestion(),
  getPrescriptionAlreadyAtPharmacyQuestion(),
  getHowLongIsValidQuestion(),
  getAvailabilityQuestion(),
  getAreAllMedicationsDeliveredQuestion(),
  getOtherWaysToSubmitQuestion(),
  getHowFastDeliveryQuestion(),
  getNotAtHomeQuestion(),
]

export const RxLpChFaq: FC = () => {
  const config = useGlobalConfigContext()
  const faqQuestionsWithLinks = faqQuestions(config)

  return (
    <div className="tablet:flex tablet:justify-center">
      <div
        className={clsx(
          styles.faq,
          'container px-4 py-6 tablet:px-0 tablet:py-9',
        )}
      >
        <span className={styles['faq-title']}>
          <FormattedMessage id="rx.ch.faq.title" />
        </span>
        <AccordionStack>
          {faqQuestionsWithLinks.map(({ answer, key, question }) => (
            <Accordion key={key}>
              <AccordionSummary>{question}</AccordionSummary>
              <AccordionDetails className="prose">{answer}</AccordionDetails>
            </Accordion>
          ))}
        </AccordionStack>
      </div>
    </div>
  )
}
